/**
 * Video player for uploaded footage. Plays the signed R2 URL returned by callout-api and
 * exposes its <video> element to the sync engine (the engine anchors on this element's
 * `currentTime`, plan §1.3a).
 */
import type { RefObject } from 'react';
import { formatClock } from './upload';
import type { FootageAsset, SignedMedia } from '../api/client';

export interface VideoPlayerProps {
  footage: FootageAsset | null;
  media: SignedMedia | null;
  videoRef: RefObject<HTMLVideoElement>;
}

export function VideoPlayer({ footage, media, videoRef }: VideoPlayerProps) {
  if (!footage || !media) {
    return (
      <section className="rounded-lg border border-chalk-200 bg-white/70 p-5 shadow-sm">
        <h2 className="mb-3 text-lg font-semibold tracking-tight">Footage</h2>
        <p data-testid="player-empty" className="text-sm text-charcoal-500">
          Upload footage to load the player.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-chalk-200 bg-white/70 p-5 shadow-sm">
      <h2 className="mb-3 text-lg font-semibold tracking-tight">Footage</h2>
      <video
        ref={videoRef}
        data-testid="footage-player"
        src={media.url}
        controls
        playsInline
        preload="metadata"
        className="w-full rounded bg-charcoal"
      />
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <dt className="text-charcoal-500">Filename</dt>
        <dd className="truncate" data-testid="player-filename">
          {footage.filename}
        </dd>
        <dt className="text-charcoal-500">Duration</dt>
        <dd data-testid="player-duration">{formatClock(footage.duration_seconds ?? 0)}</dd>
        <dt className="text-charcoal-500">Resolution</dt>
        <dd data-testid="player-resolution">{footage.resolution ?? 'unknown'}</dd>
        <dt className="text-charcoal-500">Signed link</dt>
        <dd className="text-xs text-charcoal-500">expires {media.expiresAt.slice(11, 19)} UTC</dd>
      </dl>
    </section>
  );
}
